import React from 'react'

/**
 * props:
 * - facets: { [facetKey: string]:
 *      EITHER Array<{ value: string, count: number }>
 *      OR     { [value: string]: number }
 *   }
 * - selected: { [facetKey: string]: Set<string> }
 * - onToggle(facetKey, value)
 * - disabled: boolean
 */
export default function FiltersPanel({ facets = {}, selected = {}, onToggle, disabled }) {
  const keys = Object.keys(facets)

  if (keys.length === 0) {
    return (
      <div className={`rounded border bg-white ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="border-b px-3 py-2 text-sm font-medium">Filters</div>
        <div className="p-3 text-sm text-gray-500">No facets available.</div>
      </div>
    )
  }

  // Normalize each facet to an array of { value, count }
  const normalize = (items) => {
    if (Array.isArray(items)) return items
    if (items && typeof items === 'object') {
      return Object.entries(items).map(([value, count]) => ({ value, count }))
    }
    return []
  }

  return (
    <div className={`rounded border bg-white ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="border-b px-3 py-2 text-sm font-medium">Filters</div>
      <div className="p-3 space-y-4">
        {keys.map((facet) => {
          const items = normalize(facets[facet])
            // Optional: sort by count desc, then value asc
            .sort((a, b) => (b.count - a.count) || String(a.value).localeCompare(String(b.value)))
          const chosen = selected[facet] || new Set()
          return (
            <div key={facet} className="space-y-2">
              <div className="text-xs font-medium uppercase text-gray-600">{facet}</div>
              <div className="space-y-1">
                {items.map(({ value, count }) => {
                  const id = `${facet}:${value}`
                  const checked = chosen.has(value)
                  return (
                    <label key={id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={checked}
                        onChange={() => onToggle?.(facet, value)}
                        disabled={disabled}
                      />
                      <span className="flex-1 truncate" title={String(value)}>{String(value)}</span>
                      <span className="text-gray-500 tabular-nums">{count}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
